import fetch from '@ixigo/fetch';
import { getUserLocation } from '../location-services';
import type { TripType } from '../trip-type';
import type { Coords } from '../types';
import type {
	ApiResponse,
	LocationInputFieldType,
	LocationSuggestion,
	LocationSuggestionResponse,
	LocationSuggestionsResult,
} from './types';

type LocationSuggestionsPayload = {
	query: string;
	// Backend derives airports-only vs locations-only from these two
	tripType?: TripType;
	inputFieldType?: LocationInputFieldType;
	userCoords?: { latitude: number; longitude: number };
	pickupCoords?: { latitude: number; longitude: number };
	dropOffCoords?: { latitude: number; longitude: number };
};

export async function getLocationsSuggestions(
	payload: LocationSuggestionsPayload,
	opts: { signal: AbortSignal },
): Promise<LocationSuggestionsResult> {
	const res = await fetch
		.post('cabs/api/v1/autocomplete', {
			json: payload,
			signal: opts.signal,
		})
		.json<ApiResponse<LocationSuggestionResponse>>();
	return {
		items: res?.data?.suggestions ?? [],
		categories: res?.data?.metadata?.categories ?? [],
		displayMessage: res?.data?.displayMessage,
	};
}

const mapCoords = (value: Coords | undefined) =>
	value ? { latitude: value.lat, longitude: value.lng } : undefined;
export async function getLocations(
	query: string,
	opts: {
		signal: AbortSignal;
		tripType?: TripType;
		inputFieldType?: LocationInputFieldType;
		locationBias?: Coords;
		pickupCoords?: Coords;
		dropOffCoords?: Coords;
	},
) {
	const location = opts.locationBias || (await getUserLocation());
	return getLocationsSuggestions(
		{
			query,
			tripType: opts.tripType,
			inputFieldType: opts.inputFieldType,
			userCoords: location ? mapCoords(location) : undefined,
			pickupCoords: opts.pickupCoords
				? mapCoords(opts.pickupCoords)
				: undefined,
			dropOffCoords: opts.dropOffCoords
				? mapCoords(opts.dropOffCoords)
				: undefined,
		},
		{ ...opts },
	);
}

export async function getPlaceDetails(placeId: string) {
	const res = await fetch
		.get('cabs/api/v1/placeDetails', {
			searchParams: { placeId },
		})
		.json<ApiResponse<Required<LocationSuggestion>>>();
	return res.data;
}
